import React, { useState } from 'react';
import * as outcomeStyles from './OutcomesPanel.module.scss';
import {
  Heading,
  Text,
  Link,
  Modal,
  ModalHeader,
  ModalBody,
  List,
  ListItem,
  ModalFooter,
  Button,
} from 'components/core';
import { Stack } from 'components/layout';

type ResultDataPopoverTextProps = {
  weak?: boolean;
};

const ResultDataPopoverText = ({ weak }: ResultDataPopoverTextProps) => {
  const [modal, setModal] = useState<React.ReactElement | null>();
  const variant = weak ? 'weak' : 'neutral';

  const showDefaultMeasuresModal = (e: React.MouseEvent) => {
    e.preventDefault();

    setModal(
      <Modal isOpen={true} onClose={() => setModal(null)}>
        <ModalHeader>Result data formats</ModalHeader>
        <ModalBody>
          <Stack spacing={4}>
            <Text>
              To make it easier to extract result data, we{"'"}ve added the most
              common data formats for you to use:
            </Text>
            <div>
              <Heading type="h5">Continuous data</Heading>
              <List>
                <ListItem>Mean</ListItem>
                <ListItem>Standard deviation (SD)</ListItem>
                <ListItem>95% confidence interval (95% CI)</ListItem>
                <ListItem>Standard error (SE)</ListItem>
                <ListItem>Total number of participants (N)</ListItem>
                <ListItem>Median</ListItem>
                <ListItem>Interquartile range (IQR)</ListItem>
                <ListItem>Range</ListItem>
                <ListItem>Mean difference (MD)</ListItem>
                <ListItem>Standardised mean difference (SMD)</ListItem>
                <ListItem>p-value</ListItem>
              </List>
            </div>
            <div>
              <Heading type="h5">Dichotomous data</Heading>
              <List>
                <ListItem>Number of participants with event (n)</ListItem>
                <ListItem>Percentage of participants with event (%)</ListItem>
                <ListItem>Total number of participants (N)</ListItem>
                <ListItem>Hazard ratio (HR)</ListItem>
                <ListItem>Log hazard ratio (Log HR)</ListItem>
                <ListItem>Odds ratio (OR)</ListItem>
                <ListItem>Log odds ratio (Log OR)</ListItem>
                <ListItem>Risk ratio (RR)</ListItem>
                <ListItem>Log risk ratio (Log RR)</ListItem>
                <ListItem>Risk difference (RD)</ListItem>
                <ListItem>95% confidence interval (95% CI)</ListItem>
                <ListItem>Standard error (SE)</ListItem>
                <ListItem>p-value</ListItem>
              </List>
            </div>
            <div>
              <Heading type="h5">Qualitative data</Heading>
              <List>
                <ListItem>Findings</ListItem>
                <ListItem>Theme</ListItem>
                <ListItem>Quote</ListItem>
              </List>
            </div>
            <Text>
              If you need to capture something else, use the {'"'}Add custom
              {'"'} button on the template to add your own.
            </Text>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button type="brand" onClick={() => setModal(null)}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  return (
    <>
      {modal}
      <Text variant={variant} className={outcomeStyles.outcomeDetailsText}>
        For each outcome reported in the study, you can collect result data by
        selecting from a list of{' '}
        <Link
          className={outcomeStyles.outcomeDetailsModalLink}
          component="a"
          variant="primary"
          onClick={showDefaultMeasuresModal}
        >
          common data formats (mean, SD, total etc)
        </Link>
        {'. '}
        If the format you need is not listed, you can manually add a custom one
        below.
      </Text>
    </>
  );
};

export default ResultDataPopoverText;
